import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FieldLabelRowComponent } from './field-label-row.component';

@Component({
  selector: 'formly-field-bsn',
  template: `
    <mat-form-field class="dd-compact-field w-full" [class.mat-form-field-invalid]="showError">
      <mat-label>
        <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
      </mat-label>
      <input
        matInput
        type="text"
        inputmode="numeric"
        maxlength="9"
        [required]="props.required"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [formlyDataCy]="field"
        [placeholder]="props.placeholder || ''"
        (keydown)="onKeyDown($event)"
        (paste)="onPaste($event)"
        (blur)="onBlur()" />
      @if (props.description) {
        <mat-hint>{{ props.description }}</mat-hint>
      }
      @if (showError) {
        <mat-error>{{ errorMessage }}</mat-error>
      }
    </mat-form-field>
  `,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    FormlyDataCyDirective,
    FieldLabelRowComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyBsnType extends FieldType implements OnInit {
  private readonly allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

  ngOnInit(): void {
    // If the stored value is a number, convert to zero-padded 9-digit string for display
    const currentValue = this.formControl.value;
    if (typeof currentValue === 'number') {
      this.formControl.setValue(String(currentValue).padStart(9, '0'), { emitEvent: false });
    }

    this.formControl.setValidators(FormlyBsnType.bsnValidator);
    this.formControl.updateValueAndValidity();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    if (!this.allowedKeys.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text');
    if (!pastedText) return;

    const digitsOnly = pastedText.replace(/\D/g, '').substring(0, 9);
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const current = this.formControl.value?.toString() || '';

    const newValue = (current.substring(0, start) + digitsOnly + current.substring(end)).substring(0, 9);
    this.formControl.setValue(newValue);
    this.formControl.markAsDirty();
  }

  onBlur(): void {
    const value = this.formControl.value?.toString().trim() || '';
    if (!value || !/^\d{9}$/.test(value)) {
      this.formControl.markAsTouched();
      return;
    }

    const padded = value.padStart(9, '0');
    if (padded === '000000000' || FormlyBsnType.isValidElfproef(padded)) {
      this.formControl.setValue(parseInt(value, 10));
    }
    this.formControl.markAsTouched();
  }

  get showError(): boolean {
    return this.formControl.invalid && (this.formControl.touched || this.formControl.dirty);
  }

  get errorMessage(): string {
    if (!this.formControl.errors) return '';
    if (this.formControl.errors['required']) return 'Dit veld is verplicht';
    if (this.formControl.errors['bsn']) return this.formControl.errors['bsn'];
    return 'Ongeldige waarde';
  }

  static bsnValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Numbers lose leading zeros when stored, so pad back to 9 digits
    const str = typeof value === 'number' ? String(value).padStart(9, '0') : String(value).trim();

    if (!/^\d{9}$/.test(str)) {
      return { bsn: 'BSN moet precies 9 cijfers bevatten' };
    }

    // "000000000" (numeric 0) is allowed as workaround for people without BSN
    if (str === '000000000') {
      return null;
    }

    if (!FormlyBsnType.isValidElfproef(str)) {
      return { bsn: 'Ongeldig ingevoerde BSN-nummer' };
    }

    return null;
  }

  static isValidElfproef(bsn: string): boolean {
    const weights = [9, 8, 7, 6, 5, 4, 3, 2, -1];
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(bsn[i], 10) * weights[i];
    }
    return sum % 11 === 0 && sum !== 0;
  }
}
